/**
 * Модалка строки раздела «Экология» (ecology-sites): «Внести имеющийся документ» (Проектные работы) /
 * «Отметить сданный отчёт» (Отчётность) / «Отметить выполненный контроль» (Лабораторный контроль,
 * ecology-lab-control) на выбранных площадках и правка одной строки. Для внесения предлагаются позиции
 * справочника того подраздела, из которого открыта модалка. Документы,
 * сделанные до работы с Центрикой или другим исполнителем, клиент вносит сам: достаточно отметить,
 * что документ есть, и его дату; скан прикрепляется потом в строке (необязательно).
 *
 * Поля по виду позиции: документ — «есть» с датой разработки (срок действия считается по справочнику,
 * можно указать вручную) или «нет, нужен»; отчётность — «сдаём» с датой последней сдачи (у «по
 * программе ПЭК» — ещё периодичность на площадке) или «не сдаём»; «ведётся постоянно» — «ведётся / не
 * ведётся» без дат. Правила — зеркало `domain.NormalizeEcoItemInput`, сервер проверяет авторитетно.
 *
 * Поля карточки — свойство позиции справочника (ecology-card-fields, файл заказчика 23.09.2026):
 * скрытых у позиции встроенных полей в карточке нет, подписи встроенных полей — из позиции,
 * дополнительные поля (кол-во источников, код объекта и категория площадки) — после полей вида.
 * Дополнительные поля заполняются по площадке. При внесении сразу на несколько площадок числа
 * (кол-во источников) вводятся по каждой отмеченной площадке (ecology-sources-by-site: «всего 29:
 * Офис — 15, Склад — 14»), остальные поля заполняют потом в карточке строки. Если строка позиции на
 * площадке уже есть, её значения подставлены — повторное внесение их не стирает. Рядом с числом —
 * сумма по организации, когда значения есть больше чем на одной площадке. «Код объекта и категория»
 * хранятся в площадке — правка здесь меняет площадку.
 *
 * «Отходы по ФККО» (ecology-fkko) — список пар «код + наименование» с подсказками справочника
 * (`FkkoListField`); сохраняется кнопкой карточки вместе с остальными полями, при внесении — только
 * на одну площадку.
 *
 * Файловые поля (ecology-card-file-fields) — у уже внесённой строки: файл уходит на сервер сразу
 * после выбора, отдельно от «Сохранить», и обзор раздела обновляется без закрытия карточки. Поэтому
 * форма сбрасывается только при открытии карточки (или переходе к другой строке), а не при каждом
 * обновлении обзора — несохранённый ввод не теряется. У новой строки файлы прикрепляют после
 * внесения.
 *
 * `readOnly` — карточка только для просмотра: так клиент открывает строку, которую заполнила
 * Центрика по заказу (править её может только менеджер). Поля выключены одним `fieldset disabled`,
 * кнопки сохранения нет.
 */
import { Fragment, useEffect, useId, useMemo, useState, type FormEvent } from 'react'
import type {
  EcoBuiltinField,
  EcoCardField,
  EcoCatalogItem,
  EcoFieldFile,
  EcoItemDataInput,
  EcoOverview,
  EcoPeriod,
  EcoPresence,
  EcoScopeApi,
  EcoSection,
  EcoSite,
  EcoSiteCategory,
  EcoSiteItem,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  ECO_SITE_CATEGORY_OPTIONS,
  ecoFieldIsFile,
  ecoSectionKind,
  ecoYearsLabel,
  formatEcoDate,
} from '@/lib/ecology'
import { cn } from '@/lib/utils'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field, RequiredMark } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { FieldFiles } from './field-files'
import {
  FkkoListField,
  fkkoEntriesFrom,
  fkkoRowErrors,
  fkkoRowsFrom,
  type FkkoRow,
} from './fkko-list'

const SELECTED_OPTION = 'border-accent bg-accent-bg text-accent-txt hover:bg-accent-bg'

/** Периодичность на площадке для «по программе ПЭК». */
const PEK_PERIODS: { value: Exclude<EcoPeriod, '' | 'by_pek' | 'continuous'>; label: string }[] = [
  { value: 'yearly', label: 'ежегодно' },
  { value: 'quarterly', label: 'ежеквартально' },
  { value: 'monthly', label: 'ежемесячно' },
]

/** Сегодня в ISO (локальная дата) — верхняя граница дат разработки и сдачи. */
function todayIso(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Дата + N лет в ISO (для подсказки о сроке действия). */
function addYearsIso(iso: string, years: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  return m ? `${Number(m[1]) + years}-${m[2]}-${m[3]}` : ''
}

const EMPTY_DATA: EcoItemDataInput = {
  presence: 'present',
  developedOn: '',
  validUntil: '',
  lastSubmittedOn: '',
  periodOverride: '',
  docNumber: '',
  note: '',
}

/** Данные формы из строки: срок действия вручную показываем, только если он отличается от расчётного. */
function dataFrom(item: EcoSiteItem): EcoItemDataInput {
  const computed =
    item.validityYears !== null && item.developedOn
      ? addYearsIso(item.developedOn, item.validityYears)
      : ''
  return {
    presence: item.presence,
    developedOn: item.developedOn,
    validUntil: item.validUntil && item.validUntil !== computed ? item.validUntil : '',
    lastSubmittedOn: item.lastSubmittedOn,
    periodOverride: item.periodOverride,
    docNumber: item.docNumber,
    note: item.note,
  }
}

interface Errors {
  catalogItemId?: string
  siteIds?: string
  developedOn?: string
  validUntil?: string
  lastSubmittedOn?: string
  /** Ошибки дополнительных полей по ключу поля. */
  values?: Record<string, string>
  /** Ошибки отходов по ФККО: ключ поля → «номер.code|name» (ecology-fkko). */
  fkko?: Record<string, Record<string, string>>
}

/** Код объекта НВОС и категория площадки — значение поля `site_nvos`. */
interface SiteNvos {
  nvosCode: string
  category: EcoSiteCategory
}

/** Целое число поля `number` — как проверяет сервер (`domain.NormalizeEcoFieldValues`). */
const FIELD_NUMBER_RE = /^\d{1,9}$/

/** Значение поля строки, если у строки наличие «есть» / «сдаём» (иначе поле в карточке скрыто). */
function presentValue(row: EcoSiteItem | undefined, key: string): string {
  return row?.presence === 'present' ? (row.values[key] ?? '') : ''
}

/** Дополнительные поля карточки позиции в порядке справочника. */
function ExtraFields({
  fields,
  values,
  errors,
  nvos,
  totals,
  files,
  filesDisabled,
  fkko,
  fkkoErrors,
  readOnly,
  onValue,
  onNvos,
  onUploadFile,
  onDeleteFile,
  onFkko,
}: {
  fields: EcoCardField[]
  values: Record<string, string>
  errors: Record<string, string>
  nvos: SiteNvos
  /** Сумма числового поля по организации по ключу поля (ecology-sources-by-site). */
  totals: Record<string, string>
  /** Файлы строки по ключу поля; null — строка ещё не внесена, файлы прикрепляют потом. */
  files: Record<string, EcoFieldFile[]> | null
  filesDisabled: boolean
  /** Отходы по ФККО по ключу поля (ecology-fkko). */
  fkko: (key: string) => FkkoRow[]
  fkkoErrors: Record<string, Record<string, string>>
  readOnly: boolean
  onValue: (key: string, value: string) => void
  onNvos: (next: SiteNvos) => void
  onUploadFile: (key: string, file: File) => Promise<void>
  onDeleteFile: (key: string, fileId: string) => Promise<void>
  onFkko: (key: string, rows: FkkoRow[]) => void
}) {
  const fileFields = fields.filter((f) => ecoFieldIsFile(f.type))
  return (
    <>
      {fields.map((f, i) => (
        <Fragment key={f.key}>
          {/* Подзаголовок группы — перед первым полем группы (ecology-card-corrections: «Проект
              СЗЗ», «Замеры на границе СЗЗ»…). */}
          {f.group && f.group !== fields[i - 1]?.group ? (
            <h3 className="mb-1 mt-5 text-13 font-semibold uppercase tracking-wide text-txt-tertiary">
              {f.group}
            </h3>
          ) : null}
          {ecoFieldIsFile(f.type) ? (
            files ? (
              <FieldFiles
                key={f.key}
                field={f}
                files={files[f.key] ?? []}
                disabled={filesDisabled}
                onUpload={(file) => onUploadFile(f.key, file)}
                onDelete={(fileId) => onDeleteFile(f.key, fileId)}
              />
            ) : null
          ) : f.type === 'fkko_list' ? (
            <FkkoListField
              key={f.key}
              field={f}
              rows={fkko(f.key)}
              errors={fkkoErrors[f.key] ?? {}}
              disabled={readOnly}
              onChange={(rows) => onFkko(f.key, rows)}
            />
          ) : f.type === 'number' ? (
            <Field
              key={f.key}
              label={f.label}
              hint={
                [f.hint || (f.required ? '' : 'Необязательно'), totals[f.key] ?? '']
                  .filter(Boolean)
                  .join(' · ') || undefined
              }
              required={f.required}
              error={errors[f.key]}
            >
              {(props) => (
                <Input
                  {...props}
                  className="max-w-40"
                  inputMode="numeric"
                  value={values[f.key] ?? ''}
                  invalid={Boolean(errors[f.key])}
                  onChange={(e) => onValue(f.key, e.target.value)}
                />
              )}
            </Field>
          ) : (
            <div key={f.key} className="my-4">
              <Field label={f.label} hint={f.hint || undefined} className="mb-2 mt-0">
                {(props) => (
                  <Input
                    {...props}
                    className="max-w-xs"
                    value={nvos.nvosCode}
                    placeholder="00-0000-000000-П"
                    onChange={(e) => onNvos({ ...nvos, nvosCode: e.target.value })}
                  />
                )}
              </Field>
              <div
                className="flex flex-wrap gap-2"
                role="group"
                aria-label="Категория объекта НВОС"
              >
                {ECO_SITE_CATEGORY_OPTIONS.map((o) => {
                  const on = nvos.category === o.value
                  return (
                    <Button
                      key={o.value || 'unknown'}
                      type="button"
                      size="sm"
                      variant="secondary"
                      aria-pressed={on}
                      className={cn('min-w-12', on && SELECTED_OPTION)}
                      onClick={() => onNvos({ ...nvos, category: o.value })}
                    >
                      {o.label}
                    </Button>
                  )
                })}
              </div>
            </div>
          )}
        </Fragment>
      ))}
      {!files && fileFields.length > 0 ? (
        <p className="my-3 text-12 text-txt-tertiary">
          {fileFields.map((f) => `«${f.label}»`).join(', ')} — файлы прикрепляются в карточке строки
          после внесения.
        </p>
      ) : null}
    </>
  )
}

/**
 * Числовые поля при внесении на несколько площадок (ecology-sources-by-site): у каждой отмеченной
 * площадки своё значение, под списком — сумма по организации.
 */
function SiteNumberFields({
  fields,
  sites,
  valueOn,
  errors,
  totals,
  onValue,
}: {
  fields: EcoCardField[]
  sites: EcoSite[]
  valueOn: (siteId: string, key: string) => string
  /** Ошибки по «площадка:ключ». */
  errors: Record<string, string>
  totals: Record<string, string>
  onValue: (siteId: string, key: string, value: string) => void
}) {
  return (
    <>
      {fields.map((f) => {
        const hint = [f.hint || (f.required ? '' : 'Необязательно'), totals[f.key] ?? '']
          .filter(Boolean)
          .join(' · ')
        return (
          <fieldset key={f.key} className="my-4 min-w-0 border-0 p-0">
            <legend className="mb-1.5 text-13 text-txt-secondary">
              {f.label}
              {f.required ? <RequiredMark /> : null}
              <span className="ml-1 text-12 text-txt-tertiary">— по каждой площадке</span>
            </legend>
            <div className="flex flex-col gap-2">
              {sites.map((site) => {
                const error = errors[`${site.id}:${f.key}`]
                return (
                  <div key={site.id}>
                    <div className="flex items-center gap-3">
                      <span className="min-w-0 flex-1 truncate text-13 text-txt">{site.name}</span>
                      <Input
                        className="max-w-32"
                        inputMode="numeric"
                        aria-label={`${f.label} — ${site.name}`}
                        value={valueOn(site.id, f.key)}
                        invalid={Boolean(error)}
                        onChange={(e) => onValue(site.id, f.key, e.target.value)}
                      />
                    </div>
                    {error ? (
                      <p role="alert" className="mt-1 text-right text-12 text-red">
                        {error}
                      </p>
                    ) : null}
                  </div>
                )
              })}
            </div>
            {hint ? <p className="mt-1.5 text-12 text-txt-tertiary">{hint}</p> : null}
          </fieldset>
        )
      })}
    </>
  )
}

export function ItemDialog({
  open,
  section,
  item,
  catalog,
  sites,
  items,
  defaultSiteId,
  readOnly = false,
  api,
  onClose,
  onSaved,
  onOverviewChange,
}: {
  open: boolean
  /** Подраздел, из которого открыта карточка: он задаёт вид строки и позиции для внесения. */
  section: EcoSection
  /** Правка строки; null — внесение новой. */
  item: EcoSiteItem | null
  /** Действующий справочник (для выбора позиции при внесении). */
  catalog: EcoCatalogItem[]
  sites: EcoSite[]
  /** Строки раздела — значения позиции на других площадках (подстановка при внесении, суммы). */
  items: EcoSiteItem[]
  /** Площадка, с которой открыли внесение; null — отмечены все площадки. */
  defaultSiteId: string | null
  /** Только просмотр: строку заполнила Центрика, клиент её не правит. */
  readOnly?: boolean
  api: EcoScopeApi
  onClose: () => void
  onSaved: (overview: EcoOverview) => void
  /** Обзор обновился без закрытия карточки (прикрепили или удалили файл поля). */
  onOverviewChange: (overview: EcoOverview) => void
}) {
  const formId = useId()
  const options = useMemo(
    () => catalog.filter((c) => c.section === section && c.tracked && !c.trainingLinked),
    [catalog, section],
  )
  const [catalogItemId, setCatalogItemId] = useState('')
  const [siteIds, setSiteIds] = useState<string[]>([])
  const [data, setData] = useState<EcoItemDataInput>(EMPTY_DATA)
  // Правка числовых полей по площадке; поля без правки берут значение строки позиции на площадке.
  const [drafts, setDrafts] = useState<Record<string, Record<string, string>>>({})
  // Правка отходов по ФККО по площадке; без правки — отходы строки позиции на площадке.
  const [fkkoDrafts, setFkkoDrafts] = useState<Record<string, Record<string, FkkoRow[]>>>({})
  // Правка кода объекта и категории; null — не правили, показываются значения площадки.
  const [nvosDraft, setNvosDraft] = useState<SiteNvos | null>(null)
  const [showErrors, setShowErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  // Идут загрузки или удаления файлов полей: сохранение ждёт их — иначе ответ загрузки, пришедший
  // после сохранения, вернул бы в раздел обзор без сохранённых правок.
  const [fileOps, setFileOps] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // Сброс — при открытии и при переходе к другой строке. Обновление обзора (свежая строка после
  // загрузки файла поля, новые площадки) несохранённый ввод не сбрасывает.
  const itemId = item?.id ?? null
  useEffect(() => {
    if (!open) return
    setCatalogItemId(item ? item.catalogItemId : '')
    setSiteIds(item ? [item.siteId] : defaultSiteId ? [defaultSiteId] : sites.map((s) => s.id))
    setData(item ? dataFrom(item) : EMPTY_DATA)
    setDrafts({})
    setFkkoDrafts({})
    setNvosDraft(null)
    setShowErrors(false)
    setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemId])

  // Параметры позиции: при правке — из строки (позиция могла уйти из справочника), при внесении — из
  // выбранной позиции справочника.
  const selected = options.find((o) => o.id === catalogItemId)
  const period: EcoPeriod = item ? item.catalogPeriod : (selected?.period ?? '')
  const validityYears = item ? item.validityYears : (selected?.validityYears ?? null)
  const validityNote = item ? item.validityNote : (selected?.validityNote ?? '')
  const itemName = item ? item.name : (selected?.name ?? '')
  // Настройка карточки позиции: при правке — по позиции строки (у архивной настройки нет —
  // показываются все встроенные поля).
  const position = item ? catalog.find((c) => c.id === item.catalogItemId) : selected
  const hidden = new Set<string>(position?.hiddenFields ?? [])
  const customLabel = (field: EcoBuiltinField) => position?.fieldLabels[field] ?? ''
  const extraFields = position?.fields ?? []
  // Дополнительные поля — по одной площадке: у каждой площадки своё значение.
  const targetSiteId = item ? item.siteId : siteIds.length === 1 ? siteIds[0] : null
  const targetSite = sites.find((s) => s.id === targetSiteId) ?? null
  const numberFields = extraFields.filter((f) => f.type === 'number')
  // Строка позиции на площадке: при правке — сама строка, при внесении — уже внесённая (если есть).
  const rowOn = (siteId: string) =>
    item && item.siteId === siteId
      ? item
      : items.find((i) => i.siteId === siteId && i.catalogItemId === catalogItemId)
  // Площадки, чьи значения вводятся в этой форме.
  const formSites = item ? [item.siteId] : siteIds
  const valueOn = (siteId: string, key: string) =>
    drafts[siteId]?.[key] ?? rowOn(siteId)?.values[key] ?? ''
  const valuesOn = (siteId: string) =>
    Object.fromEntries(numberFields.map((f) => [f.key, valueOn(siteId, f.key)]))
  const setValueOn = (siteId: string, key: string, value: string) =>
    setDrafts((d) => ({ ...d, [siteId]: { ...d[siteId], [key]: value } }))
  // «Всего по организации: 29 (Офис — 15, Склад — 14)» — когда значения есть больше чем на одной
  // площадке: у площадок формы — введённое, у остальных — из их строк с наличием «есть» / «сдаём»
  // (у «нет, нужен» поле скрыто, прежнее значение в сумму не идёт).
  const totals: Record<string, string> = {}
  for (const f of numberFields) {
    const parts: { name: string; n: number }[] = []
    for (const site of sites) {
      const raw = (
        formSites.includes(site.id) ? valueOn(site.id, f.key) : presentValue(rowOn(site.id), f.key)
      ).trim()
      if (FIELD_NUMBER_RE.test(raw)) parts.push({ name: site.name, n: Number(raw) })
    }
    if (parts.length > 1) {
      const sum = parts.reduce((acc, p) => acc + p.n, 0)
      totals[f.key] =
        `всего по организации: ${sum} (${parts.map((p) => `${p.name} — ${p.n}`).join(', ')})`
    }
  }
  const nvos: SiteNvos = nvosDraft ?? {
    nvosCode: targetSite?.nvosCode ?? '',
    category: targetSite?.category ?? '',
  }
  // Выбрали другую площадку — правка кода и категории прежней площадки не переносится.
  useEffect(() => {
    setNvosDraft(null)
  }, [targetSiteId])

  const isDocument = ecoSectionKind(section) === 'document'
  const isLab = section === 'lab'
  const continuous = period === 'continuous'
  const present = data.presence === 'present'
  const showExtra = extraFields.length > 0 && targetSite !== null && present
  // Внесение на несколько площадок: числа — по каждой отмеченной площадке.
  const bySite = !item && siteIds.length > 1 && present && numberFields.length > 0
  // Поля, которые при внесении на несколько площадок заполняют потом в карточке строки.
  const laterFields = extraFields.filter((f) => f.type !== 'number')
  const fkkoFields = extraFields.filter((f) => f.type === 'fkko_list')
  const fkkoOn = (siteId: string, key: string) =>
    fkkoDrafts[siteId]?.[key] ?? fkkoRowsFrom(rowOn(siteId)?.fkko[key] ?? [])
  const today = todayIso()

  const errors: Errors = {}
  if (!item && !catalogItemId)
    errors.catalogItemId = isDocument
      ? 'Выберите документ'
      : isLab
        ? 'Выберите вид контроля'
        : 'Выберите отчёт'
  if (!item && siteIds.length === 0) errors.siteIds = 'Отметьте хотя бы одну площадку'
  const hasItem = Boolean(item || selected)
  if (hasItem && isDocument && present) {
    // Переподписанная дата (у постановки на учёт — дата актуализации) — нейтральные сообщения.
    const custom = customLabel('developed_on') !== ''
    if (!data.developedOn) errors.developedOn = custom ? 'Укажите дату' : 'Укажите дату разработки'
    else if (data.developedOn > today)
      errors.developedOn = custom
        ? 'Дата не может быть в будущем'
        : 'Дата разработки не может быть в будущем'
    if (data.validUntil && data.developedOn && data.validUntil < data.developedOn)
      errors.validUntil = 'Срок действия раньше даты разработки'
  }
  if (hasItem && !isDocument && present && !continuous) {
    if (!data.lastSubmittedOn) errors.lastSubmittedOn = 'Укажите дату сдачи'
    else if (data.lastSubmittedOn > today)
      errors.lastSubmittedOn = 'Дата сдачи не может быть в будущем'
  }
  // Ошибки чисел — по ключу поля (одна площадка) или «площадка:ключ» (несколько площадок).
  const checkValues = (siteId: string, keyOf: (key: string) => string) => {
    const out: Record<string, string> = {}
    for (const f of numberFields) {
      const v = valueOn(siteId, f.key).trim()
      if (!v) {
        if (f.required) out[keyOf(f.key)] = 'Заполните поле'
      } else if (!FIELD_NUMBER_RE.test(v)) out[keyOf(f.key)] = 'Укажите целое число'
    }
    return out
  }
  const valueErrors =
    showExtra && targetSiteId
      ? checkValues(targetSiteId, (key) => key)
      : bySite
        ? Object.assign({}, ...siteIds.map((id) => checkValues(id, (key) => `${id}:${key}`)))
        : {}
  if (Object.keys(valueErrors).length > 0) errors.values = valueErrors
  if (showExtra && targetSiteId) {
    const fkkoErrors: Record<string, Record<string, string>> = {}
    for (const f of fkkoFields) {
      const e = fkkoRowErrors(fkkoOn(targetSiteId, f.key))
      if (Object.keys(e).length > 0) fkkoErrors[f.key] = e
    }
    if (Object.keys(fkkoErrors).length > 0) errors.fkko = fkkoErrors
  }
  const shown: Errors = showErrors ? errors : {}

  const title = item
    ? `${itemName} — ${sites.find((s) => s.id === item.siteId)?.name ?? ''}`
    : isDocument
      ? 'Внести имеющийся документ'
      : isLab
        ? 'Отметить выполненный контроль'
        : 'Отметить сданный отчёт'

  const presenceOptions: { value: EcoPresence; label: string }[] = isDocument
    ? [
        { value: 'present', label: 'Документ есть' },
        { value: 'needed', label: 'Документа нет, нужен' },
      ]
    : continuous
      ? [
          { value: 'present', label: 'Ведётся' },
          { value: 'needed', label: 'Не ведётся' },
        ]
      : [
          { value: 'present', label: 'Сдаём' },
          { value: 'needed', label: 'Не сдаём' },
        ]

  function termHint(): string {
    if (validityYears === null) return validityNote || 'бессрочно'
    if (data.developedOn) {
      return `по справочнику — до ${formatEcoDate(addYearsIso(data.developedOn, validityYears))} (${ecoYearsLabel(validityYears)})`
    }
    return `по справочнику — ${ecoYearsLabel(validityYears)} с даты разработки`
  }

  async function fileOp(action: () => Promise<EcoOverview>) {
    setFileOps((n) => n + 1)
    try {
      onOverviewChange(await action())
    } finally {
      setFileOps((n) => n - 1)
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving || fileOps > 0) return
    if (Object.keys(errors).length > 0) {
      setShowErrors(true)
      return
    }
    setError(null)
    setSaving(true)
    const body: EcoItemDataInput = {
      presence: data.presence,
      developedOn: isDocument && present ? data.developedOn : '',
      validUntil: isDocument && present && !hidden.has('valid_until') ? data.validUntil : '',
      lastSubmittedOn: !isDocument && present && !continuous ? data.lastSubmittedOn : '',
      periodOverride: period === 'by_pek' ? data.periodOverride : '',
      docNumber: isDocument && present && !hidden.has('doc_number') ? data.docNumber.trim() : '',
      note: data.note.trim(),
    }
    const trimmed = (siteId: string) =>
      Object.fromEntries(Object.entries(valuesOn(siteId)).map(([k, v]) => [k, v.trim()]))
    if (showExtra && targetSiteId) {
      body.values = trimmed(targetSiteId)
      if (fkkoFields.length > 0)
        body.fkko = Object.fromEntries(
          fkkoFields.map((f) => [f.key, fkkoEntriesFrom(fkkoOn(targetSiteId, f.key))]),
        )
    }
    try {
      // Код объекта и категория хранятся в площадке: правку сохраняем в площадку до строки —
      // ошибка площадки не оставит строку сохранённой наполовину.
      if (
        showExtra &&
        targetSite &&
        extraFields.some((f) => f.type === 'site_nvos') &&
        (nvos.nvosCode.trim() !== targetSite.nvosCode || nvos.category !== targetSite.category)
      ) {
        await api.updateSite(targetSite.id, {
          name: targetSite.name,
          address: targetSite.address,
          nvosCode: nvos.nvosCode.trim(),
          category: nvos.category,
        })
      }
      const overview = item
        ? await api.updateItem(item.id, body)
        : await api.createItems({
            ...body,
            catalogItemId,
            siteIds,
            ...(bySite
              ? { siteValues: Object.fromEntries(siteIds.map((id) => [id, trimmed(id)])) }
              : {}),
          })
      onSaved(overview)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) onClose()
      }}
    >
      <DialogContent title={title} className="max-w-xl">
        <Card
          scrollable
          className="max-h-dialog"
          header={title}
          footer={
            <div className="flex flex-wrap items-center justify-end gap-2">
              {!item && !readOnly ? (
                <span className="mr-auto text-12 text-txt-tertiary">
                  Скан можно прикрепить потом в строке
                </span>
              ) : null}
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                {readOnly ? 'Закрыть' : 'Отмена'}
              </Button>
              {readOnly ? null : (
                <Button type="submit" form={formId} disabled={saving || fileOps > 0}>
                  {saving
                    ? 'Сохранение…'
                    : item
                      ? 'Сохранить'
                      : isDocument
                        ? 'Внести документ'
                        : 'Сохранить'}
                </Button>
              )}
            </div>
          }
        >
          <form id={formId} onSubmit={submit} noValidate>
            {readOnly ? (
              <Alert variant="info">
                Строку заполнила Центрика по заказу — изменить её может менеджер.
              </Alert>
            ) : null}
            {/* fieldset выключает разом все поля карточки: в просмотре ничего не правится. */}
            <fieldset disabled={readOnly} className="m-0 min-w-0 border-0 p-0">
              {!item ? (
                <p className="text-13 text-txt-secondary">
                  {isDocument
                    ? 'Документ, который сделали до работы с Центрикой или у другого исполнителя. Скан загружать не обязательно — достаточно отметить, что документ есть, и его дату.'
                    : 'Отчёт, который сдали сами или с другим исполнителем. Следующий срок кабинет посчитает по календарю отчётности.'}
                </p>
              ) : null}
              {error ? (
                <Alert variant="danger" className="mt-3">
                  {error}
                </Alert>
              ) : null}

              {!item ? (
                <Field
                  label={
                    isDocument
                      ? 'Документ'
                      : isLab
                        ? 'Лабораторный контроль'
                        : 'Отчёт или мероприятие'
                  }
                  required
                  error={shown.catalogItemId}
                >
                  {(props) => (
                    <Select
                      value={catalogItemId}
                      onValueChange={(id) => {
                        setCatalogItemId(id)
                        setDrafts({})
                        setFkkoDrafts({})
                      }}
                    >
                      <SelectTrigger
                        id={props.id}
                        className="text-left"
                        aria-label={
                          isDocument
                            ? 'Документ'
                            : isLab
                              ? 'Лабораторный контроль'
                              : 'Отчёт или мероприятие'
                        }
                        aria-describedby={props['aria-describedby']}
                        invalid={Boolean(shown.catalogItemId)}
                      >
                        <SelectValue placeholder="Выберите из справочника" />
                      </SelectTrigger>
                      <SelectContent>
                        {options.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              ) : null}

              {hasItem ? (
                <>
                  <div className="my-4 flex flex-wrap gap-2" role="group" aria-label="Наличие">
                    {presenceOptions.map((o) => (
                      <Button
                        key={o.value}
                        type="button"
                        size="sm"
                        variant="secondary"
                        aria-pressed={data.presence === o.value}
                        className={cn(data.presence === o.value && SELECTED_OPTION)}
                        onClick={() => setData({ ...data, presence: o.value })}
                      >
                        {o.label}
                      </Button>
                    ))}
                  </div>

                  {isDocument && present ? (
                    <div className="grid grid-cols-1 gap-4 nw:grid-cols-2">
                      <Field
                        label={customLabel('developed_on') || 'Дата разработки'}
                        required
                        error={shown.developedOn}
                        className={cn('my-0', hidden.has('valid_until') && 'nw:col-span-2')}
                      >
                        {(props) => (
                          <DateInput
                            {...props}
                            value={data.developedOn}
                            max={today}
                            invalid={Boolean(shown.developedOn)}
                            onChange={(developedOn) => setData((d) => ({ ...d, developedOn }))}
                          />
                        )}
                      </Field>
                      {hidden.has('valid_until') ? null : (
                        <Field
                          label={customLabel('valid_until') || 'Действует до'}
                          hint={`Если указано в документе; пусто — ${termHint()}`}
                          error={shown.validUntil}
                          className="my-0"
                        >
                          {(props) => (
                            <DateInput
                              {...props}
                              value={data.validUntil}
                              invalid={Boolean(shown.validUntil)}
                              onChange={(validUntil) => setData((d) => ({ ...d, validUntil }))}
                            />
                          )}
                        </Field>
                      )}
                      {hidden.has('doc_number') ? null : (
                        <Field
                          label={customLabel('doc_number') || 'Номер, кем выдан'}
                          hint="Необязательно"
                          className="my-0 nw:col-span-2"
                        >
                          {(props) => (
                            <Input
                              {...props}
                              value={data.docNumber}
                              onChange={(e) => setData({ ...data, docNumber: e.target.value })}
                            />
                          )}
                        </Field>
                      )}
                    </div>
                  ) : null}

                  {!isDocument && present && !continuous ? (
                    <div className="grid grid-cols-1 gap-4 nw:grid-cols-2">
                      <Field
                        label={customLabel('last_submitted_on') || 'Дата последней сдачи'}
                        required
                        error={shown.lastSubmittedOn}
                        className="my-0"
                      >
                        {(props) => (
                          <DateInput
                            {...props}
                            value={data.lastSubmittedOn}
                            max={today}
                            invalid={Boolean(shown.lastSubmittedOn)}
                            onChange={(lastSubmittedOn) =>
                              setData((d) => ({ ...d, lastSubmittedOn }))
                            }
                          />
                        )}
                      </Field>
                      {period === 'by_pek' ? (
                        <Field
                          label="Периодичность на площадке"
                          hint="По программе ПЭК"
                          className="my-0"
                        >
                          {(props) => (
                            <Select
                              value={data.periodOverride || 'yearly'}
                              onValueChange={(v) =>
                                setData({ ...data, periodOverride: v as EcoPeriod })
                              }
                            >
                              <SelectTrigger
                                id={props.id}
                                className="text-left"
                                aria-label="Периодичность на площадке"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PEK_PERIODS.map((p) => (
                                  <SelectItem key={p.value} value={p.value}>
                                    {p.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </Field>
                      ) : null}
                    </div>
                  ) : null}

                  {showExtra ? (
                    <ExtraFields
                      fields={extraFields}
                      values={targetSiteId ? valuesOn(targetSiteId) : {}}
                      totals={totals}
                      errors={shown.values ?? {}}
                      nvos={nvos}
                      files={item ? item.files : null}
                      filesDisabled={readOnly || saving}
                      fkko={(key) => (targetSiteId ? fkkoOn(targetSiteId, key) : [])}
                      fkkoErrors={shown.fkko ?? {}}
                      readOnly={readOnly}
                      onFkko={(key, rows) => {
                        if (targetSiteId)
                          setFkkoDrafts((d) => ({
                            ...d,
                            [targetSiteId]: { ...d[targetSiteId], [key]: rows },
                          }))
                      }}
                      onValue={(key, value) => {
                        if (targetSiteId) setValueOn(targetSiteId, key, value)
                      }}
                      onNvos={setNvosDraft}
                      onUploadFile={async (key, file) => {
                        if (item) await fileOp(() => api.uploadFieldFile(item.id, key, file))
                      }}
                      onDeleteFile={async (key, fileId) => {
                        if (item) await fileOp(() => api.deleteFieldFile(item.id, key, fileId))
                      }}
                    />
                  ) : null}
                  {laterFields.length > 0 && present && !item && siteIds.length > 1 ? (
                    <p className="my-3 text-12 text-txt-tertiary">
                      {laterFields.map((f) => `«${f.label}»`).join(', ')} — у каждой площадки своё
                      значение: заполните в карточке строки после внесения.
                    </p>
                  ) : null}

                  <Field label={customLabel('note') || 'Примечание'} hint="Необязательно">
                    {(props) => (
                      <Input
                        {...props}
                        value={data.note}
                        onChange={(e) => setData({ ...data, note: e.target.value })}
                      />
                    )}
                  </Field>
                </>
              ) : null}

              {!item ? (
                <div className="my-4">
                  <p className="mb-2 text-13 text-txt-secondary">
                    Площадки
                    <RequiredMark />
                  </p>
                  <div className="flex flex-col gap-2.5" role="group" aria-label="Площадки">
                    {sites.map((s) => (
                      <LabeledCheckbox
                        key={s.id}
                        checked={siteIds.includes(s.id)}
                        onCheckedChange={(on) =>
                          setSiteIds((prev) =>
                            on ? [...prev, s.id] : prev.filter((id) => id !== s.id),
                          )
                        }
                      >
                        {s.name}
                      </LabeledCheckbox>
                    ))}
                  </div>
                  {shown.siteIds ? (
                    <p role="alert" className="mt-1.5 text-12 text-red">
                      {shown.siteIds}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {bySite ? (
                <SiteNumberFields
                  fields={numberFields}
                  sites={sites.filter((s) => siteIds.includes(s.id))}
                  valueOn={valueOn}
                  errors={shown.values ?? {}}
                  totals={totals}
                  onValue={setValueOn}
                />
              ) : null}
            </fieldset>
          </form>
        </Card>
      </DialogContent>
    </Dialog>
  )
}
